import { memo, VFC } from 'react';
import styled, { css } from 'styled-components';

import { NAVBAR_EXPANDED_WIDTH } from 'dashboard/components/Navbar';
import { BASE } from 'design-system/variables/spacing';

export const CONNECTOR_WIDTH = 60;
export const BRANCH_CONNECTOR_WIDTH = 20;

export interface PathHighlightInformation {
  // The base color for the branches
  branchColor?: string;
  // The highlight color for the branches that
  // overrides the base `branchColor` where the
  // highlight should be used
  highlightColor?: string;
  runPathIndexes?: number[];
  highlightPercentage?: number;
  branchStrokeWidth?: number;
}

const disableDragSelectStyling = css`
  /* disable user interaction (tree nodes individually re-enable) */
  pointer-events: none;
  /* prevent panning from getting stuck due to inadvertent selection on select or drag */
  user-select: none;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Current non-prefixed version */
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  -ms-user-drag: none;

  > * {
    -webkit-user-drag: none;
    -khtml-user-drag: none;
    -moz-user-drag: none;
    -o-user-drag: none;
    -ms-user-drag: none;
  }
`;

export const ScreenFlowContainer = styled.div<{
  height: number;
  width: number;
}>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  min-width: calc(100vw - ${NAVBAR_EXPANDED_WIDTH}px);
  height: ${({ height }) => height}px;
  width: ${({ width }) => width}px;
  transform-origin: 0 0;
  ${disableDragSelectStyling}
`;

export const ScreenFlowStepContainer = styled.div.attrs<{ height: number }>(({ height }) => ({
  style: { height },
}))<{
  height: number;
}>`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  min-width: auto;
  z-index: 1;
`;

export const ScreenFlowHorizontalLine = styled.div.attrs<{
  height?: number;
  strokeWidth?: number;
  color?: string;
}>(({ height, strokeWidth, color, theme }) => {
  const lineColor = color ?? theme.color.primary.brand200;
  const distanceLeft = (height ?? 10) / 2 - (strokeWidth ?? 2) / 2;
  const distanceRight = (height ?? 10) / 2 + (strokeWidth ?? 2) / 2;
  return {
    style: {
      height,
      width: `${BRANCH_CONNECTOR_WIDTH}px`,
      flex: '1 0 0%',
      background: `
      linear-gradient(
        0deg,
        rgba(0, 0, 0, 0) ${distanceLeft}px,
        ${lineColor} ${distanceLeft}px,
        ${lineColor} ${distanceRight}px,
        rgba(0, 0, 0, 0) ${distanceRight}px
      )
      `,
    },
  };
})<{
  height?: number;
  strokeWidth?: number;
  color?: string;
}>``;

const StepLineContainer = styled.div.attrs<{ height: number; width?: number }>(
  ({ height, width }) => ({
    style: { height, width: width ?? BASE * 4 },
  }),
)<{ height: number; width?: number }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledPath = styled.path<{ color?: string; strokeWidth?: number }>`
  stroke: ${({ color, theme }) => color ?? theme.color.primary.brand200};
  stroke-width: ${({ strokeWidth }) => strokeWidth ?? 2};
`;

export const ScreenFlowConnector = memo(
  ({
    height,
    width,
    color,
    strokeWidth,
  }: {
    height: number;
    width?: number;
    color?: string;
    strokeWidth?: number;
  }) => {
    const lineWidth = width ? width : CONNECTOR_WIDTH;
    return (
      <StepLineContainer data-test='screen-flow__step-connector' height={height} width={lineWidth}>
        <svg
          width={lineWidth}
          height='16'
          viewBox={`0 0 ${lineWidth} 2`}
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <StyledPath d={`M 0 1 L ${lineWidth} 1`} color={color} strokeWidth={strokeWidth} />
        </svg>
      </StepLineContainer>
    );
  },
);
ScreenFlowConnector.displayName = 'ScreenFlowConnector';

const CurveContainer = styled.div.attrs<{ height: number }>(({ height }) => ({
  style: { height },
}))<{
  height: number;
  initial?: boolean;
}>`
  display: flex;
  justify-content: ${({ initial }) => (initial ? 'flex-start' : 'center ')};
  margin-left: -3px;
`;

const BranchCurveContainer = styled.div.attrs<{ height: number }>(({ height }) => ({
  style: { height },
}))<{
  height: number;
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ScreenCurveSvg = styled.svg`
  transform: rotate(180deg);
  margin-top: ${BASE * -2}px;
`;

interface BranchCurveProps {
  height: number;
  color?: string;
  strokeWidth?: number;
}

const BranchCurve: VFC<BranchCurveProps> = memo(({ height, color, strokeWidth }) => {
  return (
    <BranchCurveContainer height={height}>
      <ScreenCurveSvg
        xmlns='http://www.w3.org/2000/svg'
        width='17'
        height='17'
        viewBox='0 0 17 17'
        fill='none'
      >
        <StyledPath d='M16 17C16 8.16344 8.83656 1 0 1' color={color} strokeWidth={strokeWidth} />
      </ScreenCurveSvg>
    </BranchCurveContainer>
  );
});
BranchCurve.displayName = 'BranchCurve';

interface VerticalBranchingLineProps {
  height: number;
  padding: number;
  color?: string;
  highlightColor?: string;
  highlightPercentage?: number;
  strokeWidth?: number;
}

const VerticalBranchingLine: VFC<VerticalBranchingLineProps> = memo(
  ({ height, padding, color, highlightPercentage, highlightColor, strokeWidth }) => {
    return (
      <OuterLine height={height} padding={padding}>
        <InnerLine color={color} />
        {highlightPercentage != null && (
          <HighlightWrapper padding={padding}>
            <RunPathHighlight
              color={highlightColor}
              percentage={highlightPercentage}
              strokeWidth={strokeWidth}
            />
          </HighlightWrapper>
        )}
      </OuterLine>
    );
  },
);
VerticalBranchingLine.displayName = 'VerticalBranchingLine';

const OuterLine = styled.div.attrs<{ height: number; padding: number }>(({ height, padding }) => ({
  style: {
    height,
    paddingTop: padding,
    paddingBottom: padding + 16,
  },
}))<{ height: number; padding: number }>`
  position: relative;
`;

const InnerLine = styled.div<{ color?: string }>`
  height: 100%;
  width: 1px;
  border-left: 2px solid ${({ color, theme }) => color ?? theme.color.primary.brand200};
`;

const HighlightWrapper = styled.div.attrs<{ padding: number }>(({ padding }) => ({
  style: {
    paddingTop: padding,
    paddingBottom: padding + 16,
  },
}))<{ padding: number }>`
  position: absolute;
  height: 100%;
  top: 0px;
`;

const RunPathHighlight = styled.div.attrs<{ percentage: number; strokeWidth?: number }>(
  ({ percentage }) => ({
    style: {
      height: `calc(${percentage}% + 8px)`,
    },
  }),
)<{ percentage: number; color?: string; strokeWidth?: number }>`
  width: 1px;
  border-left: ${({ color, theme, strokeWidth }) =>
    `${strokeWidth ?? 2}px solid ${color ?? theme.color.primary.brand600}`};
`;

interface ScreenFlowBranchConnectorsProps {
  currentToken: string;
  childTokens: string[];
  height: number;
  runPathIndexes?: number[];
  color?: string;
  highlightColor?: string;
  highlightPercentage?: number;
  strokeWidth?: number;
}

export const ScreenFlowBranchConnectors: VFC<ScreenFlowBranchConnectorsProps> = memo(
  ({
    currentToken,
    childTokens,
    height,
    runPathIndexes,
    color,
    highlightColor,
    highlightPercentage,
    strokeWidth,
  }) => {
    const branchOuts = childTokens.map((token, i) => {
      // highlightColor takes precedence
      let branchOutColor = highlightColor || color;
      if (runPathIndexes != null) {
        // only set color when rendering inquiry run if runPathIndexes includes branch connector index
        branchOutColor = runPathIndexes.includes(i) ? highlightColor : undefined;
      }
      if (childTokens.length === 1) {
        return (
          <CurveContainer key={`${currentToken}-${token}-${i}`} height={height} initial={true}>
            <ScreenFlowHorizontalLine
              height={height}
              color={branchOutColor}
              strokeWidth={strokeWidth}
            />
          </CurveContainer>
        );
      }

      if (i === 0) {
        return (
          <CurveContainer key={`${currentToken}-${token}-${i}`} height={height} initial={true}>
            <ScreenFlowHorizontalLine
              height={height}
              color={branchOutColor}
              strokeWidth={strokeWidth}
            />
          </CurveContainer>
        );
      } else if (i === childTokens.length - 1) {
        return (
          <CurveContainer key={`${currentToken}-${token}-${i}`} height={height}>
            <BranchCurve height={height} color={branchOutColor} strokeWidth={strokeWidth} />
          </CurveContainer>
        );
      } else {
        return (
          <CurveContainer key={`${currentToken}-${token}-${i}`} height={height}>
            <ScreenFlowHorizontalLine
              height={height}
              color={branchOutColor}
              strokeWidth={strokeWidth}
            />
          </CurveContainer>
        );
      }
    });

    return (
      <>
        <VerticalBranchingLine
          height={childTokens.length * height}
          padding={height / 2}
          color={color}
          highlightColor={highlightColor}
          highlightPercentage={highlightPercentage}
          strokeWidth={strokeWidth}
        />
        <ScreenFlowBranchStepsContainer>{branchOuts}</ScreenFlowBranchStepsContainer>
      </>
    );
  },
);
ScreenFlowBranchConnectors.displayName = 'ScreenFlowBranchConnectors';

export const ScreenFlowBranchContainer = styled.div.attrs<{ height: number }>(({ height }) => ({
  style: {
    gridTemplateRows: `repeat(auto-fill, ${height}px)`,
    gridTemplateColumns: `repeat(auto-fill, auto)`,
  },
}))<{ height: number }>`
  display: grid;
`;

export const ScreenFlowBranchStepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const ScreenFlowBranchEmptySpacer = styled.div.attrs<{ height: number }>(({ height }) => ({
  style: { height },
}))<{
  height: number;
}>``;
